import { arrayIsEqualDeep } from "utilities/array/array";

import {
    type FilterEntity,
    getSelectedFilterItemsFromSavedFilter,
    type SelectedFilterItem,
} from "entity/filters/Filter/Filter.api.types";
import { type SavedFilterItem } from "entity/filters/SavedFilter/SavedFilter.api.types";

export interface IFilterReducerState extends IFilterHistoryState {
    showHasUnsavedChanges: boolean;
}

export interface IFilterHistoryState {
    appliedFilter: SelectedFilterItem[] | undefined;
    appliedFiltersFromSavedFilter: boolean;
    selectedFilterId: number | undefined;
}

export const defaultFilterState: IFilterReducerState = {
    appliedFilter: undefined,
    showHasUnsavedChanges: false,
    appliedFiltersFromSavedFilter: false,
    selectedFilterId: undefined,
};

export class ClearFilterState {
    public readonly type = "clearFilterStateAction";
}

export class SetUnsavedAppliedFilter {
    constructor(filters: SelectedFilterItem[] = [], isChanged: boolean) {
        this.data = filters;
        this.isChanged = isChanged;
    }

    public data: SelectedFilterItem[];
    public isChanged: boolean;
    public readonly type = "setUnsavedAppliedFilterAction";
}

export class UpdateUnsavedChanges {
    constructor(
        newFilters: SelectedFilterItem[],
        filterEntity: FilterEntity,
        savedFilter: SavedFilterItem,
        applyFilterItems?: boolean,
    ) {
        this.newFilters = newFilters;
        this.filterEntity = filterEntity;
        this.savedFilter = savedFilter;
        this.applyFilterItems = applyFilterItems ?? false;
    }

    public newFilters: SelectedFilterItem[];
    public filterEntity: FilterEntity;
    public savedFilter: SavedFilterItem;
    public applyFilterItems: boolean;
    public readonly type = "updateUnsavedChangesAction";
}

export type FilterActions = SetUnsavedAppliedFilter | UpdateUnsavedChanges | ClearFilterState;

export function FilterReducer(
    state: IFilterReducerState,
    action: FilterActions,
): IFilterReducerState {
    switch (action.type) {
        case "setUnsavedAppliedFilterAction":
            return {
                ...state,
                showHasUnsavedChanges: action.isChanged,
                appliedFiltersFromSavedFilter: action.isChanged,
                appliedFilter: action.data !== undefined ? [...action.data] : undefined,
            };
        case "updateUnsavedChangesAction":
            const appliedFiltersNotFromSavedFilter = !arrayIsEqualDeep(
                action.newFilters.map((v) => v.selectedValue),
                getSelectedFilterItemsFromSavedFilter(action.filterEntity, action.savedFilter).map(
                    (v) => v.selectedValue,
                ),
            );

            const newState = {
                ...state,
                selectedFilterId: action.savedFilter.id,
                showHasUnsavedChanges: appliedFiltersNotFromSavedFilter,
                appliedFiltersFromSavedFilter: !appliedFiltersNotFromSavedFilter,
            };

            if (action.applyFilterItems) {
                newState.appliedFilter = action.newFilters;
            }

            return newState;

        case "clearFilterStateAction":
            return {
                ...defaultFilterState,
            };
        default:
            return state;
    }
}
